import { createClient } from '@supabase/supabase-js'

// Validate environment variables
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('❌ Missing Supabase environment variables!');
  console.error('REACT_APP_SUPABASE_URL:', supabaseUrl ? '✅ Set' : '❌ Missing');
  console.error('REACT_APP_SUPABASE_ANON_KEY:', supabaseAnonKey ? '✅ Set' : '❌ Missing');
  console.error('Please set these environment variables in your .env file or deployment platform.');
  console.error('For local development, create a .env file with:');
  console.error('REACT_APP_SUPABASE_URL=https://tfkhydguwdnojvaoiyqo.supabase.co');
  console.error('REACT_APP_SUPABASE_ANON_KEY=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InRma2h5ZGd1d2Rub2p2YW9peXFvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTYzMjQzMjAsImV4cCI6MjA3MTkwMDMyMH0.Jx9Njeq5ggMzZLvQWsOVzsgeT0FY-pNfCwce8fYccnI');
}

// Create Supabase client with fallback values
export const supabase = createClient(
  supabaseUrl || 'https://tfkhydguwdnojvaoiyqo.supabase.co',
  supabaseAnonKey || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InRma2h5ZGd1d2Rub2p2YW9peXFvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTYzMjQzMjAsImV4cCI6MjA3MTkwMDMyMH0.Jx9Njeq5ggMzZLvQWsOVzsgeT0FY-pNfCwce8fYccnI',
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true
    },
    global: {
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
        'Access-Control-Allow-Headers': 'Content-Type, Authorization'
      }
    }
  }
)

if (supabaseUrl && supabaseAnonKey) {
  console.log('✅ Supabase environment variables loaded successfully');
} else {
  console.warn('⚠️ Using fallback Supabase credentials - please set environment variables for production');
}

// Database types
export interface UserProfile {
  user_id: string
  name: string
  email: string
  subscription_type: 'demo' | 'free' | 'trial' | 'paid'
  subscription_status?: 'active' | 'cancelled' | 'expired'
  subscription_expires_at?: string
  subscription_cancelled_at?: string
  subscription_plan_type?: 'monthly' | 'annual' | 'year'
  subscription_id?: string
  stripe_customer_id?: string
  stripe_subscription_id?: string
  exam_date?: string
  state?: string
  created_at: string
  updated_at: string
}

export interface UserStats {
  user_id: string
  total_questions: number
  questions_answered: number
  correct_answers: number
  incorrect_answers: number
  current_streak: number
  max_streak: number
  accuracy: number
  progress: number
  daily_goal: number
  daily_progress: number
  weekly_accuracy: number
  exam_date?: string
  categories: Record<string, { answered: number; correct: number; total: number }>
  created_at: string
  updated_at: string
}

export interface StudySession {
  id: string
  user_id: string
  session_type: 'all' | 'bookmarked' | 'weak' | 'random' | 'category'
  session_config: {
    cardLimit?: number
    category?: string
    difficulty?: 'easy' | 'medium' | 'hard' | 'all'
    randomCount?: number
  }
  current_index: number
  shuffled_cards: number[]
  stats: {
    correct: number
    incorrect: number
    total: number
    streak: number
    maxStreak: number
  }
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface UserBookmark {
  id: string
  user_id: string
  card_id: number
  created_at: string
}

export interface UserCardProgress {
  id: string
  user_id: string
  card_id: number
  times_correct: number
  times_incorrect: number
  is_known: boolean
  last_answered?: string
  created_at: string
  updated_at: string
}

export interface PracticeTestSession {
  id: string
  user_id: string
  current_question_index: number
  user_answers: number[]
  shuffled_questions: number[]
  shuffled_answer_options?: Record<string, number[]>
  score: number
  is_completed: boolean
  state?: string
  started_at: string
  completed_at?: string
  created_at: string
  updated_at: string
}

// Helper functions for common operations
export const supabaseHelpers = {
  // Get current user
  async getCurrentUser() {
    const { data: { user }, error } = await supabase.auth.getUser()
    if (error) throw error
    return user
  },

  // Sign up user
  async signUp(email: string, password: string, name: string) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name: name
        }
      }
    })
    if (error) throw error
    return data
  },

  // Sign in user
  async signIn(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })
    if (error) throw error
    return data
  },

  // Sign out user
  async signOut() {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  },

  // Get user profile
  async getUserProfile(userId: string) {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('user_id', userId)
      .single()
    
    if (error && error.code !== 'PGRST116') throw error // PGRST116 = no rows returned
    return data
  },

  // Create or update user profile
  async upsertUserProfile(profile: Partial<UserProfile>) {
    const { data, error } = await supabase
      .from('user_profiles')
      .upsert(profile)
      .select()
      .single()
    
    if (error) throw error
    return data
  },

  // Get user stats
  async getUserStats(userId: string) {
    const { data, error } = await supabase
      .from('user_stats')
      .select('*')
      .eq('user_id', userId)
      .single()
    
    if (error && error.code !== 'PGRST116') throw error
    return data
  },

  // Create or update user stats
  async upsertUserStats(stats: Partial<UserStats>) {
    const { data, error } = await supabase
      .from('user_stats')
      .upsert(stats)
      .select()
      .single()
    
    if (error) throw error
    return data
  },

  // Study Session Management
  async getActiveStudySession(userId: string) {
    const { data, error } = await supabase
      .from('study_sessions')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true)
      .order('updated_at', { ascending: false })
      .limit(1)
      .single()
    
    if (error && error.code !== 'PGRST116') throw error
    return data
  },

  async createStudySession(session: Partial<StudySession>) {
    const { data, error } = await supabase
      .from('study_sessions')
      .insert(session)
      .select()
      .single()
    
    if (error) throw error
    return data
  },

  async updateStudySession(id: string, updates: Partial<StudySession>) {
    const { data, error } = await supabase
      .from('study_sessions')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()
    
    if (error) throw error
    return data
  },

  async deactivateStudySession(id: string) {
    const { error } = await supabase
      .from('study_sessions')
      .update({ is_active: false, updated_at: new Date().toISOString() })
      .eq('id', id)
    
    if (error) throw error
  },

  // Bookmark Management
  async getUserBookmarks(userId: string) {
    const { data, error } = await supabase
      .from('user_bookmarks')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
    
    if (error) throw error
    return data
  },

  async addBookmark(userId: string, cardId: number) {
    const { data, error } = await supabase
      .from('user_bookmarks')
      .insert({ user_id: userId, card_id: cardId })
      .select()
      .single()
    
    if (error) throw error
    return data
  },

  async removeBookmark(userId: string, cardId: number) {
    const { error } = await supabase
      .from('user_bookmarks')
      .delete()
      .eq('user_id', userId)
      .eq('card_id', cardId)
    
    if (error) throw error
  },

  async isBookmarked(userId: string, cardId: number) {
    const { data, error } = await supabase
      .from('user_bookmarks')
      .select('id')
      .eq('user_id', userId)
      .eq('card_id', cardId)
      .single()
    
    if (error && error.code !== 'PGRST116') throw error
    return !!data
  },

  // Card Progress Management
  async getCardProgress(userId: string, cardId: number) {
    const { data, error } = await supabase
      .from('user_card_progress')
      .select('*')
      .eq('user_id', userId)
      .eq('card_id', cardId)
      .single()
    
    if (error && error.code !== 'PGRST116') throw error
    return data
  },

  async updateCardProgress(progress: Partial<UserCardProgress>) {
    console.log('Updating card progress with:', progress);
    
    try {
      console.log('Starting database update...');
      
      // If we have an existing record ID, use update instead of upsert
      if (progress.id) {
        console.log('Using UPDATE for existing record with ID:', progress.id);
        const { data, error } = await supabase
          .from('user_card_progress')
          .update({ 
            times_correct: progress.times_correct,
            times_incorrect: progress.times_incorrect,
            is_known: progress.is_known,
            last_answered: progress.last_answered,
            updated_at: new Date().toISOString() 
          })
          .eq('id', progress.id)
          .select()
          .single();
        
        console.log('Database response received:', { data, error });
        
        if (error) {
          console.error('Error updating card progress:', error);
          console.error('Error details:', {
            code: error.code,
            message: error.message,
            details: error.details,
            hint: error.hint
          });
          throw error;
        }
        
        console.log('Card progress updated successfully:', data);
        return data;
      } else {
        // Use upsert for new records
        console.log('Using UPSERT for new record');
        const { data, error } = await supabase
          .from('user_card_progress')
          .upsert({ 
            ...progress, 
            updated_at: new Date().toISOString() 
          })
          .select()
          .single();
        
        console.log('Database response received:', { data, error });
        
        if (error) {
          console.error('Error updating card progress:', error);
          console.error('Error details:', {
            code: error.code,
            message: error.message,
            details: error.details,
            hint: error.hint
          });
          throw error;
        }
        
        console.log('Card progress updated successfully:', data);
        return data;
      }
    } catch (error) {
      console.error('Exception in updateCardProgress:', error);
      throw error;
    }
  },

  async getWeakCards(userId: string) {
    const { data, error } = await supabase
      .from('user_card_progress')
      .select('card_id')
      .eq('user_id', userId)
      .eq('is_known', false)
    
    if (error) throw error
    return data.map(item => item.card_id)
  },

  async getBookmarkedCards(userId: string) {
    const { data, error } = await supabase
      .from('user_bookmarks')
      .select('card_id')
      .eq('user_id', userId)
    
    if (error) throw error
    return data.map(item => item.card_id)
  },

  // Practice Test Session Management
  async getActivePracticeTestSession(userId: string) {
    const { data, error } = await supabase
      .from('practice_test_sessions')
      .select('*')
      .eq('user_id', userId)
      .eq('is_completed', false)
      .order('created_at', { ascending: false })
      .limit(1)
      .single()
    
    if (error && error.code !== 'PGRST116') throw error
    return data
  },

  async createPracticeTestSession(session: Partial<PracticeTestSession>) {
    const { data, error } = await supabase
      .from('practice_test_sessions')
      .insert(session)
      .select()
      .single()
    
    if (error) throw error
    return data
  },

  async updatePracticeTestSession(id: string, updates: Partial<PracticeTestSession>) {
    const { data, error } = await supabase
      .from('practice_test_sessions')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()
    
    if (error) throw error
    return data
  },

  async completePracticeTestSession(id: string, finalScore: number) {
    const { data, error } = await supabase
      .from('practice_test_sessions')
      .update({ 
        is_completed: true, 
        score: finalScore,
        completed_at: new Date().toISOString(),
        updated_at: new Date().toISOString() 
      })
      .eq('id', id)
      .select()
      .single()
    
    if (error) throw error
    return data
  },

  async getPracticeTestHistory(userId: string) {
    const { data, error } = await supabase
      .from('practice_test_sessions')
      .select('*')
      .eq('user_id', userId)
      .eq('is_completed', true)
      .order('completed_at', { ascending: false })
    
    if (error) throw error
    return data
  }
}
